import { Box, Button, Overlay, SingleColumnLayout, Tab, TabBar, Tabs, TabView } from '@salecycle/ui-component-library';
import { useAccessLevel, usePermissions } from 'core/app/AppContext';
import matchSorter, { rankings } from 'match-sorter';
import React, { FC, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ClientConstants } from 'sections/client-management/constants';
import { Brand, Group, Site } from 'sections/client-management/entities';
import styled from 'styled-components';
import Spinner from '../components/spinner';
import ClientList from './components/ClientList';
import RecentClients from './components/RecentClients';
import SearchBar from './components/SearchBar';
import { SearchOverlayWelcome } from './components/SearchOverlayWelcome';
import * as recentClientsUtils from './utils/recentClients';
import { useAppContext, useClients } from 'core/app/AppContext';
import {
  usePreferencesState,
  usePreferencesDispatch,
  toggleSearchOverlay,
} from 'core/preferences/usePreferencesContext';

const StyledSearchContainer = styled.div`
  width: 100%;
  margin-right: 20px;
`;

const StyledNoSitesMessage = styled.h3`
  padding: 2rem 0;
`;

const NewClientButton = styled.div`
  flex-shrink: 0;
`;

interface Results {
  sites: Site[];
  brands: Brand[];
  groups: Group[];
}

export const SearchOverlay: FC = () => {
  const { t } = useTranslation();
  const { push } = useHistory();

  const { isAppStateLoading } = useAppContext();
  const { getBrands, getGroups, getSites } = useClients();
  const { searchOverlayVisible } = usePreferencesState();
  const dispatch = usePreferencesDispatch();

  const [results, setResults] = useState<Results>(null);
  const [query, setQuery] = useState('');

  const [selectedTabId, setSelectedTabId] = useState(t<string>(ClientConstants.SITES_TAB_ID_TRANSLATIONS_LOOKUP));
  const { accessLevel } = useAccessLevel();
  const { hasPermission } = usePermissions();

  const sites = getSites();
  const brands = getBrands();
  const groups = getGroups();

  const queryExists = query.length > 0;
  const clientsPresent = sites.length + brands.length + groups.length > 0;

  const filterClients = useCallback(
    (query: string): { sites: Site[]; brands: Brand[]; groups: Group[] } => {
      if (!query || query === '') {
        return {
          sites: [],
          brands: [],
          groups: [],
        };
      }

      return {
        sites: matchSorter(sites, query, { keys: [{ threshold: rankings.EQUAL, key: 'id' }, 'name'] }),
        brands: matchSorter(brands, query, { keys: [{ threshold: rankings.EQUAL, key: 'id' }, 'name'] }),
        groups: matchSorter(groups, query, { keys: [{ threshold: rankings.EQUAL, key: 'id' }, 'name'] }),
      };
    },
    [sites, brands, groups],
  );

  useEffect(() => {
    setResults(filterClients(query));
  }, [query, filterClients]);

  const siteCount = clientsPresent && queryExists ? results?.sites.length ?? 0 : 0;
  const brandCount = clientsPresent && queryExists ? results?.brands.length ?? 0 : 0;
  const groupCount = clientsPresent && queryExists ? results?.groups.length ?? 0 : 0;

  const siteIds = clientsPresent && queryExists ? results?.sites.map(site => site.id) ?? [] : [];
  const brandIds = clientsPresent && queryExists ? results?.brands.map(site => site.id) ?? [] : [];
  const groupIds = clientsPresent && queryExists ? results?.groups.map(site => site.id) ?? [] : [];

  const recentClients = recentClientsUtils.getAllFromLocalStorage();

  const handleCloseButtonClicked = () => {
    toggleSearchOverlay(dispatch, false);
  };

  const handleSelectedTabChanged = (newSelectedTabId: string) => {
    setSelectedTabId(newSelectedTabId);
  };

  const searchQueryUpdate = (value: string) => {
    if (value !== query) {
      setQuery(value);
    }
  };

  const handleNewClientClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    toggleSearchOverlay(dispatch, false);

    push(`/create/group`);
  };

  const showSpinner = isAppStateLoading;
  const showNoSitesMessage = !showSpinner && !clientsPresent;
  const showSearch = !showSpinner && !showNoSitesMessage;

  return (
    <Overlay
      onCloseButtonClicked={handleCloseButtonClicked}
      closeText={t('client.search.closeText')}
      overlayTitle={t('client.search.title', {
        context: accessLevel === 'MultiSite' ? 'multisite' : null,
      })}
      visible={searchOverlayVisible}
    >
      <div data-testid="SearchOverlay" role="search">
        <SingleColumnLayout align="center" minWidth={920} maxWidth={920}>
          <Box stackItems={true}>
            {showSpinner && <Spinner />}

            {showNoSitesMessage && (
              <Box verticalPositioning="center">
                <StyledNoSitesMessage data-testid="NoSitesMessage">{t('client.search.noClients')}</StyledNoSitesMessage>
              </Box>
            )}

            {showSearch && (
              <>
                <Box verticalPositioning="center">
                  <StyledSearchContainer>
                    <SearchBar value={query} searchQueryUpdate={searchQueryUpdate} visible={searchOverlayVisible} />
                  </StyledSearchContainer>

                  {hasPermission('ManageClients') && (
                    <NewClientButton>
                      <Button variation="primary" type="button" onClick={handleNewClientClick}>
                        {t('common.newClient')}
                      </Button>
                    </NewClientButton>
                  )}
                </Box>
                {queryExists ? (
                  <Tabs fillHeight={true} selectedTabId={selectedTabId} onSelectedTabChanged={handleSelectedTabChanged}>
                    <TabBar>
                      {accessLevel !== 'MultiSite' && (
                        <Tab
                          tabId={t(ClientConstants.GROUPS_TAB_ID_TRANSLATIONS_LOOKUP)}
                          badgeLabel={String(groupCount)}
                        >
                          {t(ClientConstants.GROUPS_TAB_ID_TRANSLATIONS_LOOKUP)}
                        </Tab>
                      )}

                      {accessLevel !== 'MultiSite' && (
                        <Tab
                          tabId={t(ClientConstants.BRANDS_TAB_ID_TRANSLATIONS_LOOKUP)}
                          badgeLabel={String(brandCount)}
                        >
                          {t(ClientConstants.BRANDS_TAB_ID_TRANSLATIONS_LOOKUP)}
                        </Tab>
                      )}
                      <Tab tabId={t(ClientConstants.SITES_TAB_ID_TRANSLATIONS_LOOKUP)} badgeLabel={String(siteCount)}>
                        {t(ClientConstants.SITES_TAB_ID_TRANSLATIONS_LOOKUP)}
                      </Tab>
                    </TabBar>

                    <TabView padding={0}>
                      <ClientList
                        clientLevel={selectedTabId.toLowerCase()}
                        siteIds={siteIds}
                        brandIds={brandIds}
                        groupIds={groupIds}
                      />
                    </TabView>
                  </Tabs>
                ) : recentClients.length > 0 ? (
                  <RecentClients clients={recentClients} />
                ) : (
                  <SearchOverlayWelcome />
                )}
              </>
            )}
          </Box>
        </SingleColumnLayout>
      </div>
    </Overlay>
  );
};

export default SearchOverlay;
